import { z } from 'zod'

/**
 * How Housecall Pro files an address, as the live account returned them on
 * 2026-10-08: a service address or a billing address. HCP publishes no such
 * list, so this is what has been seen. A kind not here is read as
 * unrecognized rather than refused (`readHcpAddressKind`), and the customer
 * it sits on still reads.
 */
export const HCP_ADDRESS_KINDS = ['service', 'billing'] as const

export const hcpAddressKindSchema = z.enum(HCP_ADDRESS_KINDS)

export type HcpAddressKind = z.infer<typeof hcpAddressKindSchema>

/**
 * An address kind as read from HCP: one this CRM knows, or one it does not,
 * kept in HCP's words so a person can still read it. One new kind never
 * fails the rest of a customer.
 */
export const hcpAddressKindReadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('known'), value: hcpAddressKindSchema }),
  z.object({ kind: z.literal('unrecognized'), words: z.string() }),
])

export type HcpAddressKindRead = z.infer<typeof hcpAddressKindReadSchema>

/** HCP's words for an address kind, read as one this CRM knows when it is. */
export function readHcpAddressKind(
  words: string | null,
): HcpAddressKindRead | null {
  if (words === null) return null
  const known = hcpAddressKindSchema.safeParse(words)
  return known.success
    ? { kind: 'known', value: known.data }
    : { kind: 'unrecognized', words }
}

/** HCP's own words for an address kind, as shown next to the street. */
export function hcpAddressKindWords(read: HcpAddressKindRead): string {
  return read.kind === 'known' ? read.value : read.words
}
